import { baseApiClient as api } from "../baseService";
export const addTagTypes = [
  "police_departments",
  "police_records_channels",
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      policeDepartmentsList: build.query<
        PoliceDepartmentsListApiResponse,
        PoliceDepartmentsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/`,
          params: {
            do_not_call: queryArg.doNotCall,
            enrichment_status: queryArg.enrichmentStatus,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ordering: queryArg.ordering,
            search: queryArg.search,
            state_code: queryArg.stateCode,
          },
        }),
        providesTags: ["police_departments"],
      }),
      policeDepartmentsCreate: build.mutation<
        PoliceDepartmentsCreateApiResponse,
        PoliceDepartmentsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/`,
          method: "POST",
          body: queryArg.createPoliceDepartment,
        }),
        invalidatesTags: ["police_departments"],
      }),
      policeRecordsChannelsList: build.query<
        PoliceRecordsChannelsListApiResponse,
        PoliceRecordsChannelsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.departmentId}/records-channels/`,
        }),
        providesTags: ["police_records_channels"],
      }),
      policeRecordsChannelsCreate: build.mutation<
        PoliceRecordsChannelsCreateApiResponse,
        PoliceRecordsChannelsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.departmentId}/records-channels/`,
          method: "POST",
          body: queryArg.createDepartmentRecordsChannel,
        }),
        invalidatesTags: ["police_records_channels"],
      }),
      policeRecordsChannelsRetrieve: build.query<
        PoliceRecordsChannelsRetrieveApiResponse,
        PoliceRecordsChannelsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.departmentId}/records-channels/${queryArg.id}/`,
        }),
        providesTags: ["police_records_channels"],
      }),
      policeRecordsChannelsUpdate: build.mutation<
        PoliceRecordsChannelsUpdateApiResponse,
        PoliceRecordsChannelsUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.departmentId}/records-channels/${queryArg.id}/`,
          method: "PUT",
          body: queryArg.updateDepartmentRecordsChannel,
        }),
        invalidatesTags: ["police_records_channels"],
      }),
      policeRecordsChannelsDestroy: build.mutation<
        PoliceRecordsChannelsDestroyApiResponse,
        PoliceRecordsChannelsDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.departmentId}/records-channels/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["police_records_channels"],
      }),
      policeDepartmentsRetrieve: build.query<
        PoliceDepartmentsRetrieveApiResponse,
        PoliceDepartmentsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.id}/`,
        }),
        providesTags: ["police_departments"],
      }),
      policeDepartmentsUpdate: build.mutation<
        PoliceDepartmentsUpdateApiResponse,
        PoliceDepartmentsUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.id}/`,
          method: "PUT",
          body: queryArg.updatePoliceDepartment,
        }),
        invalidatesTags: ["police_departments"],
      }),
      policeDepartmentsPartialUpdate: build.mutation<
        PoliceDepartmentsPartialUpdateApiResponse,
        PoliceDepartmentsPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdatePoliceDepartment,
        }),
        invalidatesTags: ["police_departments"],
      }),
      policeDepartmentsActiveMatters: build.query<
        PoliceDepartmentsActiveMattersApiResponse,
        PoliceDepartmentsActiveMattersApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.id}/active-matters/`,
        }),
        providesTags: ["police_departments"],
      }),
      policeDepartmentsTriggerEnrichment: build.mutation<
        PoliceDepartmentsTriggerEnrichmentApiResponse,
        PoliceDepartmentsTriggerEnrichmentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/${queryArg.id}/trigger-enrichment/`,
          method: "POST",
        }),
        invalidatesTags: ["police_departments"],
      }),
      policeDepartmentsGetOrCreate: build.mutation<
        PoliceDepartmentsGetOrCreateApiResponse,
        PoliceDepartmentsGetOrCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/get_or_create/`,
          method: "POST",
          body: queryArg.basePoliceDepartment,
        }),
        invalidatesTags: ["police_departments"],
      }),
      policeDepartmentsSearch: build.query<
        PoliceDepartmentsSearchApiResponse,
        PoliceDepartmentsSearchApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/search/`,
          params: {
            q: queryArg.q,
          },
        }),
        providesTags: ["police_departments"],
      }),
      policeDepartmentsListWithStats: build.query<
        PoliceDepartmentsListWithStatsApiResponse,
        PoliceDepartmentsListWithStatsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/police-departments/with-stats/`,
          params: {
            name: queryArg.name,
            state: queryArg.state,
          },
        }),
        providesTags: ["police_departments"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type PoliceDepartmentsListApiResponse =
  /** status 200  */ ReadPoliceDepartmentWithStats[];
export type PoliceDepartmentsListApiArg = {
  /** Filter by do not call flag */
  doNotCall?: boolean;
  /** Filter by enrichment status */
  enrichmentStatus?: string;
  /** Number of results per page (default: 100, max: 500) */
  limit?: number;
  /** Starting position for pagination */
  offset?: number;
  /** Order by field (e.g., -modified_at, name, -attested_reports_count) */
  ordering?: string;
  /** Search across name, state, aliases, description */
  search?: string;
  /** Filter by state code */
  stateCode?: string;
};
export type PoliceDepartmentsCreateApiResponse =
  /** status 201  */ ReadPoliceDepartment;
export type PoliceDepartmentsCreateApiArg = {
  createPoliceDepartment: CreatePoliceDepartment;
};
export type PoliceRecordsChannelsListApiResponse =
  /** status 200  */ ReadDepartmentRecordsChannel[];
export type PoliceRecordsChannelsListApiArg = {
  departmentId: string;
};
export type PoliceRecordsChannelsCreateApiResponse =
  /** status 201  */ ReadDepartmentRecordsChannel;
export type PoliceRecordsChannelsCreateApiArg = {
  departmentId: string;
  createDepartmentRecordsChannel: CreateDepartmentRecordsChannel;
};
export type PoliceRecordsChannelsRetrieveApiResponse =
  /** status 200  */ ReadDepartmentRecordsChannel;
export type PoliceRecordsChannelsRetrieveApiArg = {
  departmentId: string;
  id: string;
};
export type PoliceRecordsChannelsUpdateApiResponse =
  /** status 200  */ ReadDepartmentRecordsChannel;
export type PoliceRecordsChannelsUpdateApiArg = {
  departmentId: string;
  id: string;
  updateDepartmentRecordsChannel: UpdateDepartmentRecordsChannel;
};
export type PoliceRecordsChannelsDestroyApiResponse = unknown;
export type PoliceRecordsChannelsDestroyApiArg = {
  departmentId: string;
  id: string;
};
export type PoliceDepartmentsRetrieveApiResponse =
  /** status 200  */ ReadPoliceDepartment;
export type PoliceDepartmentsRetrieveApiArg = {
  id: string;
};
export type PoliceDepartmentsUpdateApiResponse =
  /** status 200  */ ReadPoliceDepartment;
export type PoliceDepartmentsUpdateApiArg = {
  id: string;
  updatePoliceDepartment: UpdatePoliceDepartment;
};
export type PoliceDepartmentsPartialUpdateApiResponse =
  /** status 200  */ ReadPoliceDepartment;
export type PoliceDepartmentsPartialUpdateApiArg = {
  id: string;
  patchedUpdatePoliceDepartment: UpdatePoliceDepartment2;
};
export type PoliceDepartmentsActiveMattersApiResponse =
  /** status 200  */ ActiveMatterSummary;
export type PoliceDepartmentsActiveMattersApiArg = {
  id: string;
};
export type PoliceDepartmentsTriggerEnrichmentApiResponse = /** status 202  */ {
  status?: string;
};
export type PoliceDepartmentsTriggerEnrichmentApiArg = {
  id: string;
};
export type PoliceDepartmentsGetOrCreateApiResponse =
  /** status 200  */ ReadPoliceDepartment;
export type PoliceDepartmentsGetOrCreateApiArg = {
  basePoliceDepartment: BasePoliceDepartment;
};
export type PoliceDepartmentsSearchApiResponse =
  /** status 200  */ ReadPoliceDepartment;
export type PoliceDepartmentsSearchApiArg = {
  /** Search query for department name, state, or aliases */
  q: string;
};
export type PoliceDepartmentsListWithStatsApiResponse =
  /** status 200  */ ReadPoliceDepartmentWithStats;
export type PoliceDepartmentsListWithStatsApiArg = {
  /** Filter departments by name */
  name?: string;
  /** Filter departments by state code */
  state?: string;
};
export type ContactHandleType =
  | "PHONE"
  | "EMAIL"
  | "WEBSITE"
  | "FAX"
  | "OTHER"
  | "MAILING_ADDRESS";
export type WebSource = {
  /** The URL of the source */
  url: string;
  /** Very short display text for the source URL */
  title: string;
};
export type ContactHandleSchema = {
  id?: (string | string) | null;
  /** The captured contact information. Should contain an E.164 formatted phone number, or an email address, or a website, or a fax number. */
  value?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  handle_type: ContactHandleType;
  /** Short description of the handle; e.g. 'Billing Dept Toll-Free', 'Front Desk Fax' etc. */
  name?: string | null;
  /** Optional notes about the handle. */
  notes?: string | null;
  /** Citations for the handle. */
  sources?: WebSource[] | null;
  /** Whether this is the preferred contact handle. */
  preferred?: boolean | null;
  division_id?: (string | string) | null;
};
export type JurisdictionType =
  | "city"
  | "county"
  | "state"
  | "federal"
  | "tribal"
  | "special";
export type PoliceDepartmentEnrichmentStatus =
  | "not_enriched"
  | "enrichment_in_progress"
  | "enrichment_failed"
  | "enrichment_succeeded";
export type ServiceType =
  | "crash_reports"
  | "foia_equivalent"
  | "criminal_records"
  | "incident_reports"
  | "general_records";
export type PoliceRecordsInquiryMajorVendor =
  | "carfax_crashdocs"
  | "lexis_nexis_buycrash"
  | "independent_portal"
  | "next_request"
  | "gov_qa"
  | "not_a_portal"
  | "txdot_cris";
export type AddressSchema = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province_or_state?: string | null;
  postal_or_zip?: string | null;
  country_code?: string | null;
};
export type MailRequirement =
  | "CLIENT_PHOTO_ID_COPY"
  | "FIRM_LETTERHEAD"
  | "PROOF_OF_REPRESENTATION"
  | "COMPLETED_REQUEST_FORM"
  | "NOTARIZED_REQUEST_FORM";
export type FeeComponent = {
  /** The amount of the fee in cents. */
  amount_cents: number;
  /** One line description of the component of the total fee backed by research and assumptions.
    ex:
    - "Upper bound estimate of 20 pages at $0.25/page as specified by ____"
    - "Estimating up to 30min of labor at stated rate of $20/hour as specified by ____"
    - "Base certified crash report fee $8.44 as specified by ____"
    - "Added 7% for tax because site mentions it's not included in provided info" */
  description: string;
};
export type RecordsFeeEstimate = {
  /** An estimated fee for requesting a copy of a police report.
    Depending on the department, sometimes this is called a "crash report", "accident report",
    "incident report", or "police report".
    
    Rule: IF more than one report option is available, bias to the more expensive, or certified option.
    If fees are not defined for the department, fall back to county or state guidance on fees. */
  estimated_fee_cents?: number | null;
  /** All the components that led to the estimated fee */
  components?: FeeComponent[] | null;
  /** Full verbatim fee details including:
    - Exact language from official source
    - Conditions, alternatives, or special cases
    - Statutory citations (e.g., "per RSMo §610.026")
    - Payment instructions
    ALWAYS populate this when ANY fee information is available. */
  notes?: string | null;
  /** A 0-1 score indicating the confidence. */
  confidence_score?: number | null;
  /** 2-3 sentences explaining the confidence score. */
  reasoning?: string | null;
};
export type CheckMailingInstructions = {
  /** Verifiable instructions on how to request a police report from the agency by mail. */
  instructions?: string;
  /** The mailing address to send the request to. */
  address: AddressSchema;
  /** The best, single name of the entity to make the check payable to. (Payee)
    Do not place anything except for a single, bank-ready name here. */
  payable_to?: string | null;
  /** Any specific text required to be written on the check memo line, if explicitly mentioned, otherwise null.
    Example: "Police Report Request" or "Report #[REPORT_NUMBER]" or "Accident Report Fee"
    available variables: "[REPORT_NUMBER], [CLIENT_NAME], [DATE_OF_LOSS]" */
  memo_instructions?: string | null;
  /** The number of days after incident date to wait before requesting
    the records by mail, if explicitly specified, otherwise null. */
  wait_before_requesting_days?: number | null;
  /** Any department-specified, or state-specified, number of days to expect for processing, if explicitly stated, otherwise null. */
  estimated_processing_days?: number | null;
  /** Any items that are explicitly REQUIRED for requesting a police report by mail, if described, otherwise null. */
  required_items?: MailRequirement[] | null;
  /** Any special requirements for requesting a police report by mail, if described, otherwise null. */
  special_requirements?: string | null;
  /** The estimated set of fees to request a copy of a police report by mail.
    
    - (for any question of the report type being sought): assume we want the most detailed version of the report, sometimes this is "crash report", "accident report", "incident report", "police report", bias to the more expensive of the four
    - (for any page-based fee structure) assume a 20 page incident report
    - (for any requestor-based fee differences) assume legal representative of involved party
    - (for request-method based pricing) assume check + mail but bias to more expensive option
    - (for any charge for a missing report number) assume we do not have the report number
    - (for any charges related to labor) assume 30 minutes of labor
    - (for any charges related to certified copies) assume we are paying for the most certified copy */
  fee_structure?: RecordsFeeEstimate;
  /** Web pages that were used to gather the information. */
  citations?: WebSource[] | null;
};
export type DepartmentForm = {
  /** Concise title for the Form */
  title: string;
  /** Concise sentence description of the Form and its purpose. */
  description?: string | null;
  /** URL of the Form file */
  file_url: string;
};
export type ReadDepartmentRecordsChannel = {
  /** A concise title for the channel - e.g. "FOIA Portal", "Records Department Phone" */
  title?: string | null;
  /** Single sentence description of the channel and its purpose. */
  description?: string | null;
  /** Tags for record types that can be serviced by this channel.
    e.g. ["crash_reports", "foia_equivalent", "general_records"] */
  services_provided?: ServiceType[] | null;
  /** The type of channel: mail, phone, email, online_portal, fax */
  channel_type?: string | null;
  /** Web sources/citations for this channel information */
  citations?: WebSource[] | null;
  /** Concise instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.' */
  instructions: string;
  handle: ContactHandleSchema;
  fee_amount?: number | null;
  major_vendor?: PoliceRecordsInquiryMajorVendor;
  /** An explicitly provided link to a pdf, docx, or other static file form that the department publishes and requires
    inquirers to fill out for making police report requests. Not every PD may ask for inquirers to provide a formal request,
    but if they do, we should capture them here - otherwise null. */
  required_document_url?: WebSource;
  mailing_instructions?: CheckMailingInstructions;
  /** Department-provided forms associated with this channel */
  forms?: DepartmentForm[] | null;
  /** Portal URL if this is an online portal channel */
  portal_url?: string | null;
  /** Portal domain if this is an online portal channel */
  portal_domain?: string | null;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department_id: string | string;
  required_document_file_url?: string | null;
};
export type TimeZone = "PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST";
export type PoliceDepartmentStats = {
  total_reports_count?: number;
  attested_reports_count?: number;
  verified_reports_count?: number;
  active_matters_count?: number;
  has_mail_contact?: boolean;
  has_portal?: boolean;
  has_phone_contact?: boolean;
};
export type ReadPoliceDepartmentWithStats = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  records_channels?: ReadDepartmentRecordsChannel[];
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
  id: string | string;
  created_at: string;
  modified_at: string;
  stats: PoliceDepartmentStats;
};
export type ReadPoliceDepartment = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  records_channels?: ReadDepartmentRecordsChannel[];
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
  id: string | string;
  created_at: string;
  modified_at: string;
};
export type BaseDepartmentRecordsChannel = {
  /** A concise title for the channel - e.g. "FOIA Portal", "Records Department Phone" */
  title?: string | null;
  /** Single sentence description of the channel and its purpose. */
  description?: string | null;
  /** Tags for record types that can be serviced by this channel.
    e.g. ["crash_reports", "foia_equivalent", "general_records"] */
  services_provided?: ServiceType[] | null;
  /** The type of channel: mail, phone, email, online_portal, fax */
  channel_type?: string | null;
  /** Web sources/citations for this channel information */
  citations?: WebSource[] | null;
  /** Concise instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.' */
  instructions: string;
  /** Most direct contact detail availablefor this method of requesting records if explicitly mentioned, otherwise null. */
  handle: ContactHandleSchema;
  /** Dollar amount associated with this mail method, if specified. */
  fee_amount?: number | null;
  /** If and only if this method is via a known major vendor, mark it here - otherwise null. */
  major_vendor?: PoliceRecordsInquiryMajorVendor;
  /** An explicitly provided link to a pdf, docx, or other static file form that the department publishes and requires
    inquirers to fill out for making police report requests. Not every PD may ask for inquirers to provide a formal request,
    but if they do, we should capture them here - otherwise null. */
  required_document_url?: WebSource;
  /** If this method is via mailing, capture all the stated mailing instructions here - otherwise null. */
  mailing_instructions?: CheckMailingInstructions;
  /** Department-provided forms associated with this channel */
  forms?: DepartmentForm[] | null;
  /** Portal URL if this is an online portal channel */
  portal_url?: string | null;
  /** Portal domain if this is an online portal channel */
  portal_domain?: string | null;
};
export type CreatePoliceDepartment = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  /** Any instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.'
    
    i.e if there's a phone number for records, an email, a buycrash link, in-person hours, and mail instructions,
    there should be 5 entries here. */
  records_channels?: BaseDepartmentRecordsChannel[] | null;
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
};
export type CreateDepartmentRecordsChannel = {
  /** A concise title for the channel - e.g. "FOIA Portal", "Records Department Phone" */
  title?: string | null;
  /** Single sentence description of the channel and its purpose. */
  description?: string | null;
  /** Tags for record types that can be serviced by this channel.
    e.g. ["crash_reports", "foia_equivalent", "general_records"] */
  services_provided?: ServiceType[] | null;
  /** The type of channel: mail, phone, email, online_portal, fax */
  channel_type?: string | null;
  /** Web sources/citations for this channel information */
  citations?: WebSource[] | null;
  /** Concise instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.' */
  instructions: string;
  /** Most direct contact detail availablefor this method of requesting records if explicitly mentioned, otherwise null. */
  handle: ContactHandleSchema;
  /** Dollar amount associated with this mail method, if specified. */
  fee_amount?: number | null;
  /** If and only if this method is via a known major vendor, mark it here - otherwise null. */
  major_vendor?: PoliceRecordsInquiryMajorVendor;
  /** An explicitly provided link to a pdf, docx, or other static file form that the department publishes and requires
    inquirers to fill out for making police report requests. Not every PD may ask for inquirers to provide a formal request,
    but if they do, we should capture them here - otherwise null. */
  required_document_url?: WebSource;
  /** If this method is via mailing, capture all the stated mailing instructions here - otherwise null. */
  mailing_instructions?: CheckMailingInstructions;
  /** Department-provided forms associated with this channel */
  forms?: DepartmentForm[] | null;
  /** Portal URL if this is an online portal channel */
  portal_url?: string | null;
  /** Portal domain if this is an online portal channel */
  portal_domain?: string | null;
  police_department_id: string | string;
};
export type UpdateDepartmentRecordsChannel = {
  /** A concise title for the channel - e.g. "FOIA Portal", "Records Department Phone" */
  title?: string | null;
  /** Single sentence description of the channel and its purpose. */
  description?: string | null;
  /** Tags for record types that can be serviced by this channel.
    e.g. ["crash_reports", "foia_equivalent", "general_records"] */
  services_provided?: ServiceType[] | null;
  /** The type of channel: mail, phone, email, online_portal, fax */
  channel_type?: string | null;
  /** Web sources/citations for this channel information */
  citations?: WebSource[] | null;
  /** Concise instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.' */
  instructions: string;
  /** Most direct contact detail availablefor this method of requesting records if explicitly mentioned, otherwise null. */
  handle: ContactHandleSchema;
  /** Dollar amount associated with this mail method, if specified. */
  fee_amount?: number | null;
  /** If and only if this method is via a known major vendor, mark it here - otherwise null. */
  major_vendor?: PoliceRecordsInquiryMajorVendor;
  /** An explicitly provided link to a pdf, docx, or other static file form that the department publishes and requires
    inquirers to fill out for making police report requests. Not every PD may ask for inquirers to provide a formal request,
    but if they do, we should capture them here - otherwise null. */
  required_document_url?: WebSource;
  /** If this method is via mailing, capture all the stated mailing instructions here - otherwise null. */
  mailing_instructions?: CheckMailingInstructions;
  /** Department-provided forms associated with this channel */
  forms?: DepartmentForm[] | null;
  /** Portal URL if this is an online portal channel */
  portal_url?: string | null;
  /** Portal domain if this is an online portal channel */
  portal_domain?: string | null;
  id: string | string;
};
export type UpdatePoliceDepartment = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  /** Any instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.'
    
    i.e if there's a phone number for records, an email, a buycrash link, in-person hours, and mail instructions,
    there should be 5 entries here. */
  records_channels?: BaseDepartmentRecordsChannel[] | null;
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
  id?: (string | string) | null;
};
export type UpdatePoliceDepartment2 = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  /** Any instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.'
    
    i.e if there's a phone number for records, an email, a buycrash link, in-person hours, and mail instructions,
    there should be 5 entries here. */
  records_channels?: BaseDepartmentRecordsChannel[] | null;
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
  id?: (string | string) | null;
};
export type MatterInvestigationStatus =
  | "need_info"
  | "researching"
  | "investigating"
  | "escalated"
  | "completed_report_received"
  | "closed_no_report"
  | "not_applicable"
  | "backfilled_closed";
export type ActiveMatterSummary = {
  matter_id: string | string;
  matter_name: string;
  investigation_status: MatterInvestigationStatus;
  firm_id: string | string;
  firm_name: string;
  date_of_loss: string | null;
};
export type BasePoliceDepartment = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  /** Any instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.'
    
    i.e if there's a phone number for records, an email, a buycrash link, in-person hours, and mail instructions,
    there should be 5 entries here. */
  records_channels?: BaseDepartmentRecordsChannel[] | null;
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
};
export const {
  usePoliceDepartmentsListQuery,
  usePoliceDepartmentsCreateMutation,
  usePoliceRecordsChannelsListQuery,
  usePoliceRecordsChannelsCreateMutation,
  usePoliceRecordsChannelsRetrieveQuery,
  usePoliceRecordsChannelsUpdateMutation,
  usePoliceRecordsChannelsDestroyMutation,
  usePoliceDepartmentsRetrieveQuery,
  usePoliceDepartmentsUpdateMutation,
  usePoliceDepartmentsPartialUpdateMutation,
  usePoliceDepartmentsActiveMattersQuery,
  usePoliceDepartmentsTriggerEnrichmentMutation,
  usePoliceDepartmentsGetOrCreateMutation,
  usePoliceDepartmentsSearchQuery,
  usePoliceDepartmentsListWithStatsQuery,
} = injectedRtkApi;
